import { PageSpinner, ReactTable, DefaultColumnType } from '@td-client-frontend/common';
import { Nullable } from '@td-client-frontend/internal/src/@types/common';
import clsx from 'clsx';
import moment from 'moment';
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Member, MemberInCell, MemberStatus } from '../../../@types/Members';
import WithPermission from '../../../components/WithPermission';
import {
  getMemberStatus,
  getMemberStatusLabel,
  getMemberStatusTextClass,
  ensureDatePropertyValueIsValid
} from '../utils';
import { MemberTerminationModal } from './components';
import { Dropdown, DropdownOptions } from './Dropdown';
import MemberActivationModal from './components/MemberActivationModal';
import { Expand } from '../../../components/Expand';
import Pagination from '../../../components/Pagination';
import { Permissions } from '../../../@types/Permissions';

import { useMemberContext } from '../../../context/MemberContext';
import { MemberTabs } from '../ManageMember/MemberFormContent';
import { dateFormatter } from '../../../utils/members/fieldsFormatters';
const DATE_FORMAT = 'MM/DD/YYYY';
export interface MemberModalProps {
  isOpen: boolean;
  onClose: (state: boolean) => void;
  onComplete: () => void;
  member: Member | null;
  setSelectedMember: (member: Member | null) => void;
}

interface MembersTableProps {
  isLoading: boolean;
  memberData: any;
  searchFilter: string;
  toggleManageMemberModal: () => void;
  reFetchMembers: () => void;
  setSelectedMemberId: (val: string) => void;
  setDependentId: (val: string) => void;
  setActiveTab: (tab: string) => void;
}

function MembersTable({
  isLoading,
  reFetchMembers,
  memberData,
  searchFilter,
  toggleManageMemberModal,
  setSelectedMemberId,
  setDependentId,
  setActiveTab
}: MembersTableProps) {
  const [showTerminationModal, setShowTerminationModal] = useState<boolean>(false);
  const [showActivationModal, setShowActivationModal] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const data = memberData;
  const { setIsViewMode, itemsPerPage, setItemsPerPage, currentPage, setCurrentPage } = useMemberContext();

  const closeTerminationModal = useCallback(() => {
    setShowTerminationModal(false);
  }, [setShowTerminationModal]);
  const closeActivationModal = useCallback(() => {
    setShowActivationModal(false);
  }, [setShowActivationModal]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilter, setCurrentPage]);

  const getMemberActions = useCallback(
    (memberInfo) => {
      function getPrimaryByMrn(primaryMemberId: any) {
        if (!primaryMemberId) return undefined;
        return data?.membersSearch?.find((member: { mrnId: any }) => primaryMemberId === member.mrnId);
      }

      function isTerminated({
        coverageEndDate,
        coverageStartDate
      }: {
        coverageEndDate: string;
        coverageStartDate: string;
      }) {
        const now = moment();
        return now.isAfter(coverageEndDate) || moment(coverageEndDate).isBefore(coverageStartDate);
      }

      const { mrnId, isPrimary, primaryMemberId, readOnly } = memberInfo;
      const primaryMemberInfo = getPrimaryByMrn(primaryMemberId);
      const isPrimaryActive = isPrimary ? true : !isTerminated(primaryMemberInfo);
      const isMemberTerminated = isTerminated(memberInfo);
      const handleClick = (isViewMode: boolean = false) => {
        toggleManageMemberModal();
        setIsViewMode(isViewMode);
        if (isPrimary) {
          setSelectedMemberId(mrnId);
        } else {
          setSelectedMemberId(primaryMemberId);
          setDependentId(mrnId);
          setActiveTab(MemberTabs.Dependents);
        }
      };
      const options: DropdownOptions[] = [];
      if (isMemberTerminated && isPrimaryActive) {
        options.push({
          label: 'Reactivate',
          onClick: () => {
            setSelectedMember(memberInfo);
            setShowActivationModal(true);
          }
        });
      } else {
        options.push(
          {
            label: 'Edit',
            onClick: () => handleClick()
          },
          {
            label: 'Terminate',
            onClick: () => {
              setSelectedMember(memberInfo);
              setShowTerminationModal(true);
            }
          }
        );
      }
      return (
        <div className="flex items-center">
          <button className="pr-4" onClick={() => handleClick(true)}>
            <span className="text-primary-500">View</span>
          </button>
          {!readOnly && (isPrimary || isPrimaryActive) && <Dropdown options={options} />}
        </div>
      );
    },
    [data, setDependentId, setSelectedMemberId, setIsViewMode, toggleManageMemberModal, setActiveTab]
  );
  const columns: DefaultColumnType = React.useMemo(
    () => [
      {
        id: 'expander',
        isVisible: true,
        Cell: ({ row }) => {
          return row.canExpand ? (
            <span {...row.getToggleRowExpandedProps({})}>
              <Expand expanded={!!row.isExpanded} />
            </span>
          ) : null;
        },
        width: 50
      },
      {
        Header: 'First name',
        accessor: 'firstName',
        sortable: true
      },
      {
        Header: 'Last name',
        accessor: 'lastName'
      },
      {
        Header: 'DOB',
        Cell: ({ row }: MemberInCell) => dateFormatter(row.original.dateOfBirth),
        width: 100
      },
      {
        Header: 'Group name',
        Cell: ({ row }: MemberInCell) => {
          return row.original.group?.name ?? 'Unknown';
        }
      },
      {
        Header: 'Create date',
        Cell: ({ row }: MemberInCell) =>
          row.original.mrnCreatedAt ? moment.utc(row.original.mrnCreatedAt).format(DATE_FORMAT) : '-',
        width: 100
      },
      {
        Header: 'Effective date',
        Cell: ({ row }: MemberInCell) =>
          row.original.coverageStartDate ? moment.utc(row.original.coverageStartDate).format(DATE_FORMAT) : '-',
        width: 100
      },
      {
        Header: 'Term date',
        Cell: ({ row }: MemberInCell) =>
          row.original.coverageEndDate ? moment.utc(row.original.coverageEndDate).format(DATE_FORMAT) : '-',
        width: 100
      },
      {
        Header: 'Status',
        Cell: ({ row }: MemberInCell) => {
          const { coverageStartDate, coverageEndDate } = row.original;

          const status: Nullable<MemberStatus> = getMemberStatus(coverageStartDate, coverageEndDate);

          return (
            status && (
              <span className={clsx(getMemberStatusTextClass(status), 'capitalize')}>
                {getMemberStatusLabel(status)}
              </span>
            )
          );
        },
        width: 80
      },
      {
        Header: 'Actions',
        Cell: ({ row: { original: memberInfo } }: MemberInCell) => {
          return (
            <WithPermission requiredPerms={[Permissions.MemberRead]}>{getMemberActions(memberInfo)}</WithPermission>
          );
        },
        width: 100
      }
    ],
    [getMemberActions]
  );

  const tableData = useMemo(() => {
    return (
      data?.membersSearch
        ?.map(({ dependentMembers, ...rest }: { dependentMembers: any }) => {
          ensureDatePropertyValueIsValid(rest, 'coverageEndDate');
          return {
            ...rest,
            subRows:
              dependentMembers
                ?.map((dependent: any) => {
                  ensureDatePropertyValueIsValid(dependent, 'coverageEndDate');
                  return { ...dependent, primaryMemberId: rest.mrnId };
                })
                .reverse() ?? undefined
          };
        })
        .reverse() ?? []
    );
  }, [data]);

  return (
    <>
      {showTerminationModal && (
        <MemberTerminationModal
          isOpen={showTerminationModal}
          onClose={closeTerminationModal}
          onComplete={reFetchMembers}
          member={selectedMember}
          setSelectedMember={setSelectedMember}
        />
      )}
      {showActivationModal && (
        <MemberActivationModal
          isOpen={showActivationModal}
          onClose={closeActivationModal}
          onComplete={reFetchMembers}
          member={selectedMember}
          setSelectedMember={setSelectedMember}
        />
      )}
      <div className="flex flex-col w-full">
        {!searchFilter ? (
          <p className="text-lg">Specify member filters and click search</p>
        ) : !isLoading ? (
          <>
            <ReactTable
              columns={columns}
              data={tableData}
              subColumns={columns}
              expandSubRows={false}
              isVisibleSubRowsHeaders={false}
            />
            {data?.totalPages > 1 && (
              <Pagination
                totalPages={data?.totalPages}
                onItemsPerPageChange={setItemsPerPage}
                itemsPerPage={itemsPerPage}
                selectedPage={currentPage}
                onPageChange={setCurrentPage}
                isLoading={!tableData}
              />
            )}
          </>
        ) : (
          <PageSpinner size={60} />
        )}
      </div>
    </>
  );
}

const SuspenseWithLoader = (props: any) => {
  return (
    <Suspense fallback={<PageSpinner size={60} />}>
      <MembersTable {...props} />
    </Suspense>
  );
};
export default SuspenseWithLoader;
